import React, { useState, useEffect } from 'react';
import { QRCodeCard } from './QRCodeCard';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';
import { QRSoftDeleteService } from '../../../services/qrSoftDeleteService';
import { useAuth } from '../../../contexts/AuthContext';

export function QRCodeGrid({
  searchTerm = '',
  typeFilter = 'all',
  onSelectQR,
  selectedQRs = [],
  onBulkAction,
  onShowDetails,
  refreshTrigger = 0,
  onDelete,
  onPermanentDelete, // Added permanent delete callback
  onUpdate, // Added onUpdate callback prop for QR code updates
  qrCodes = [], // Accept QR codes from parent
  isLoading = false, // Accept loading state from parent
  error = '', // Accept error state from parent
  isTrashView = false // Whether we're in trash view
}) {
  const { user } = useAuth();
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // REMOVED: Grid no longer loads its own data, uses props from parent instead

  // Handle QR code deletion with confirmation
  const handleDeleteQR = async (qrCode) => {
    if (!qrCode?.id) return;
    
    setDeleteConfirm({
      qrCode,
      show: true
    });
  };

  // Confirm delete action - Soft delete or permanent delete based on isTrashView
  const confirmDelete = async () => {
    if (!deleteConfirm?.qrCode?.id || !user?.id) return;
    
    setDeleteLoading(true);
    
    try {
      console.log('🗑️ Attempting to delete QR code:', {
        id: deleteConfirm?.qrCode?.id,
        name: deleteConfirm?.qrCode?.name,
        userId: user?.id,
        permanent: isTrashView
      });
      
      let result;
      
      if (isTrashView) {
        // Permanent delete from trash
        result = await QRSoftDeleteService.bulkPermanentlyDeleteQRCodes([deleteConfirm?.qrCode?.id], user.id);
      } else {
        // Soft delete (move to trash)
        result = await QRSoftDeleteService.bulkSoftDeleteQRCodes([deleteConfirm?.qrCode?.id], user.id);
      }
      
      console.log('🗑️ Delete result:', result);
      
      // Check if the operation failed completely or if this specific QR code failed
      if (!result?.success || (result?.successful === 0 && result?.failed > 0)) {
        // Show specific error message to user
        const errorMsg = result?.successful === 0 && result?.failed > 0
          ? `Cannot delete "${deleteConfirm?.qrCode?.name}": ${isTrashView ? 'Error performing permanent delete' : "You don't own this QR code or it's already deleted"}` 
          : `Delete failed: ${result?.error || 'Unknown error'}`;
        alert(errorMsg);
        setDeleteConfirm(null); // Close modal
        return;
      }
      
      // Note: onDelete and onPermanentDelete callbacks removed to prevent double deletion
      
      // Success feedback
      console.log(`✅ QR code "${deleteConfirm?.qrCode?.name}" ${isTrashView ? 'permanently deleted' : 'moved to trash'} successfully`);
      
    } catch (error) {
      console.error('❌ Error deleting QR code:', error);
      alert(`Failed to delete QR code: ${error?.message || 'Unknown error'}`);
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  // Simplified filter function - remove unused filters
  const filteredQRCodes = qrCodes?.filter(qr => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      const matchesName = (qr?.name || '')?.toLowerCase()?.includes(searchLower);
      const matchesContent = (qr?.content || '')?.toLowerCase()?.includes(searchLower);
      if (!matchesName && !matchesContent) {
        return false;
      }
    }

    // Type filter only
    if (typeFilter !== 'all' && qr?.qr_type !== typeFilter) {
      return false;
    }

    return true;
  });

  const handleSelectQR = (qrId, isSelected) => {
    if (onSelectQR) {
      onSelectQR(qrId, isSelected);
    }
  };

  const handleShowDetails = (qr) => {
    if (onShowDetails) {
      onShowDetails(qr);
    }
  };

  // Handle QR code update from card edit modal - FIXED: Properly handle updates
  const handleQRUpdate = (updatedQRCode) => {
    if (!updatedQRCode?.id) return;
    
    // Update the QR code in local state immediately for responsive UI
    setQrCodes(prev => prev?.map(qr => 
      qr?.id === updatedQRCode?.id ? {
        ...qr,
        ...updatedQRCode,
        // Ensure we preserve any existing relationships
        campaigns: updatedQRCode?.campaigns || qr?.campaigns,
        trackableUrl: updatedQRCode?.trackableUrl || qr?.trackableUrl
      } : qr
    ));
    
    // Call parent onUpdate callback to notify about the update
    if (onUpdate) {
      onUpdate(updatedQRCode);
    }
    
    // Also trigger a refresh to ensure data consistency with database
    setTimeout(() => {
      setQrCodes(prevCodes => [...prevCodes]); // Force re-render
    }, 100);
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Sign In Required</h3>
          <p className="text-blue-700">
            Please sign in to view and manage your QR codes.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)]?.map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="w-full h-32 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="flex justify-between items-center">
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertTriangle" size={48} className="text-red-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load QR Codes</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <div className="space-x-4">
          <Button 
            onClick={() => window.location?.reload()}
            className="inline-flex items-center gap-2"
          >
            <Icon name="RefreshCw" size={16} />
            Try Again
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/qr-code-generator'}
            className="inline-flex items-center gap-2"
          >
            <Icon name="Plus" size={16} />
            Create Your First QR Code
          </Button>
        </div>
      </div>
    );
  }

  if (filteredQRCodes?.length === 0 && qrCodes?.length > 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Search" size={48} className="text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No QR Codes Found</h3>
        <p className="text-gray-500 mb-4">
          No QR codes match your current filters. Try adjusting your search criteria.
        </p>
        <Button 
          variant="outline" 
          onClick={() => {
            // Reset all filters - you might want to call parent callbacks here
            console.log('Reset filters');
          }}
        >
          Clear Filters
        </Button>
      </div>
    );
  }

  if (qrCodes?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="QrCode" size={48} className="text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No QR Codes Yet</h3>
        <p className="text-gray-500 mb-4">
          Create your first QR code to get started with tracking and analytics.
        </p>
        <Button 
          onClick={() => window.location.href = '/qr-code-generator'}
          className="inline-flex items-center gap-2"
        >
          <Icon name="Plus" size={16} />
          Create QR Code
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredQRCodes?.length} of {qrCodes?.length} QR codes
        </p>
        
        {selectedQRs?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Icon name="CheckSquare" size={16} />
            {selectedQRs?.length} selected
          </div>
        )}
      </div>

      {/* QR Code Grid - Now with working update functionality */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredQRCodes?.map((qr) => (
          <QRCodeCard
            key={qr?.id}
            qrCode={qr}
            isSelected={selectedQRs?.includes(qr?.id)}
            onSelect={(isSelected) => handleSelectQR(qr?.id, isSelected)}
            onShowDetails={() => handleShowDetails(qr)}
            onDelete={handleDeleteQR}
            onUpdate={handleQRUpdate}
            showSelection={selectedQRs?.length > 0}
            showCampaignInfo={true}
          />
        ))}
      </div>

      {/* Enhanced Error Display - Show delete-specific errors */}
      {error && (
        <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center">
            <Icon name="AlertTriangle" size={20} className="text-red-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Enhanced with different content for trash view */}
      {deleteConfirm?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isTrashView ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                <Icon name="Trash2" size={20} className={isTrashView ? 'text-red-600' : 'text-blue-600'} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {isTrashView ? 'Permanently Delete' : 'Move to Trash'}
                </h3>
                <p className="text-sm text-gray-500">
                  {isTrashView ? 'This action cannot be undone' : 'You can restore this later'}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                {isTrashView ? 'Permanently delete' : 'Move'}{' '}
                <span className="font-semibold">"{deleteConfirm?.qrCode?.name || 'this QR code'}"</span>
                {isTrashView ? '?' : ' to trash?'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {isTrashView 
                  ? 'This will permanently delete the QR code and all its data. This action cannot be undone.'
                  : 'This QR code will be moved to trash. You can restore it anytime from the trash.'
                }
              </p>
              {!isTrashView && (
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  💡 The QR code will continue working until permanently deleted from trash.
                </p>
              )}
              {isTrashView && (
                <p className="text-xs text-red-600 mt-2 font-medium">
                  ⚠️ Warning: The QR code will stop working immediately.
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {isTrashView ? 'Deleting Permanently...' : 'Moving to Trash...'}
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} className="mr-2" />
                    {isTrashView ? 'Delete Permanently' : 'Move to Trash'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Load More (if needed) */}
      {qrCodes?.length >= 50 && (
        <div className="text-center pt-8">
          <Button 
            variant="outline"
            onClick={() => {
              // Implement load more functionality
              console.log('Load more QR codes');
            }}
            className="inline-flex items-center gap-2"
          >
            <Icon name="Plus" size={16} />
            Load More QR Codes
          </Button>
        </div>
      )}
    </div>
  );
}